<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-1">
      <v-icon class="mr-3" color="primary">mdi-link-variant</v-icon>
      <span class="text-h5 font-weight-bold">Sources</span>
    </div>
    <div class="text-body-2 text-medium-emphasis mb-6">
      The sites AI read when answering questions about
      {{ primaryBrandName || 'this entity' }}.
    </div>

    <HistoricalDateBanner
      :visible="showSnapshotBanner"
      :loading="loadingSnapshot"
      :load-target="snapshotLoadTarget"
      :selected-date="selectedHistoricalDate"
      :error="snapshotError"
      @clear="$emit('historical-date-cleared')"
    />

    <!-- ══ State A · provider does not ground answers on the web ══════════ -->
    <v-card v-if="!isGrounded" variant="outlined" class="brand-surface-card empty-card">
      <div class="empty-inner">
        <div class="empty-icon empty-icon--muted">
          <v-icon size="26" color="grey">mdi-link-variant-off</v-icon>
        </div>
        <div class="empty-title">{{ nonGroundedCopy.title }}</div>
        <p class="empty-body">{{ nonGroundedCopy.line1 }}</p>
        <p class="empty-body">{{ nonGroundedCopy.line2 }}</p>
        <v-btn
          color="primary"
          class="mt-2"
          prepend-icon="mdi-swap-horizontal"
          @click="$emit('switch-provider', 'google_grounded')"
        >
          Switch to Gemini ⚡ (Grounded)
        </v-btn>
      </div>
    </v-card>

    <!-- ══ State B · grounded, but the scan returned zero web citations ═══ -->
    <v-card
      v-else-if="totalCites === 0"
      variant="outlined"
      class="brand-surface-card empty-card"
    >
      <div class="empty-inner">
        <div class="empty-icon empty-icon--warning">
          <v-icon size="23" color="warning">mdi-alert-circle-outline</v-icon>
        </div>
        <div class="empty-title empty-title--sm">No sources came back for this scan</div>
        <p class="empty-body">
          The scan ran on Gemini (Grounded), but the model answered without citing any
          web pages. This usually means it was confident enough to answer from training
          data alone. Try re-running the scan.
        </p>
      </div>
    </v-card>

    <!-- ══ Populated view ═══════════════════════════════════════════════ -->
    <template v-else>
      <div class="brand-eyebrow mb-2">
        Where the answer came from · {{ totalCites }}
        {{ totalCites === 1 ? 'citation' : 'citations' }}
      </div>

      <!-- Hero: owned share -->
      <v-card variant="outlined" class="brand-surface-card hero-card">
        <div class="hero-top">
          <div class="hero-figure">
            <div class="hero-pct">{{ formatPct(ownedShare) }}</div>
            <div class="hero-caption">
              of what AI cited came from
              <span class="mono strong">{{ ownedPrimaryDomain || 'your site' }}</span>
            </div>
          </div>
          <div class="hero-bar-wrap">
            <div class="hero-bar">
              <div class="hero-bar__owned" :style="{ width: barWidth(ownedShare) }"></div>
              <div class="hero-bar__rest"></div>
            </div>
            <div class="hero-bar__legend">
              <span class="strong">
                Your site · {{ ownedCites }} {{ ownedCites === 1 ? 'citation' : 'citations' }}
              </span>
              <span class="muted">
                Everyone else · {{ totalCites - ownedCites }} citations across
                {{ thirdPartySiteCount }} {{ thirdPartySiteCount === 1 ? 'site' : 'sites' }}
              </span>
            </div>
          </div>
        </div>
        <div class="hero-stats">
          <div><strong>{{ totalCites }}</strong> citations</div>
          <div><strong>{{ siteCount }}</strong> {{ siteCount === 1 ? 'site' : 'sites' }}</div>
          <div><strong>{{ queryCount }}</strong> {{ queryCount === 1 ? 'query' : 'queries' }}</div>
          <div class="muted">{{ providerLabel }} · scanned {{ scannedDateLabel }}</div>
        </div>
      </v-card>

      <!-- Source table -->
      <div class="brand-eyebrow section-eyebrow">
        <template v-if="hasLongTail">Sites AI returned to</template>
        <template v-else>Every source</template>
        · {{ mainRows.length }} {{ mainRows.length === 1 ? 'site' : 'sites' }},
        {{ mainCiteTotal }} {{ mainCiteTotal === 1 ? 'citation' : 'citations' }}
      </div>
      <v-card variant="outlined" class="brand-surface-card table-card">
        <div class="src-table" role="table">
          <div class="src-table__head" role="row">
            <div role="columnheader">Source</div>
            <div aria-hidden="true"></div>
            <div role="columnheader" class="num">Cites</div>
            <div role="columnheader" class="num">Queries</div>
          </div>
          <div
            v-for="row in mainRows"
            :key="row.key"
            class="src-table__row"
            :class="{ 'src-table__row--owned': row.owned }"
            role="row"
          >
            <div class="src-table__source" role="cell">
              <a
                :href="row.url"
                target="_blank"
                rel="noopener noreferrer"
                class="mono src-table__domain"
                :class="{ strong: row.owned }"
              >{{ row.domain }}</a>
              <span v-if="row.owned" class="owned-pill">Your site</span>
            </div>
            <div class="src-table__bar" role="cell">
              <div class="src-table__track">
                <div
                  class="src-table__fill"
                  :class="{ 'src-table__fill--owned': row.owned }"
                  :style="{ width: barWidth(row.cites / maxMainCites) }"
                ></div>
              </div>
            </div>
            <div class="num src-table__cites" role="cell">{{ row.cites }}</div>
            <div
              class="num src-table__queries"
              role="cell"
              :class="{ 'warn-text': row.concentrated }"
            >
              {{ row.queries }}<span v-if="row.concentrated" class="warn-mark"> ⚠</span>
            </div>
          </div>
        </div>
        <div v-if="concentratedNote" class="src-table__note">
          <span class="warn-mark">⚠</span>
          <span>
            Concentrated in one answer — <span class="mono">{{ concentratedNote.domain }}</span>
            was cited {{ concentratedNote.cites }} times but by a single query, so it shaped
            one response heavily rather than showing up broadly.
          </span>
        </div>
      </v-card>

      <!-- The long tail -->
      <template v-if="hasLongTail">
        <div class="brand-eyebrow section-eyebrow section-eyebrow--muted">The long tail</div>
        <v-card variant="outlined" class="brand-surface-card tail-card">
          <div class="tail-headline">
            <span class="tail-count">{{ longTailRows.length }} sites</span>
            <span class="tail-sub">
              were cited exactly once —
              {{ formatPct(longTailRows.length / totalCites) }} of every citation in this scan
            </span>
          </div>
          <p class="tail-body">
            Individually none of these is worth chasing; collectively they are a real
            slice of where AI looked.
          </p>
          <div class="tail-chips">
            <a
              v-for="row in visibleLongTail"
              :key="row.key"
              :href="row.url"
              target="_blank"
              rel="noopener noreferrer"
              class="mono tail-chip"
            >{{ row.domain }}</a>
            <button
              v-if="longTailRows.length > TAIL_PREVIEW"
              type="button"
              class="tail-toggle"
              @click="showAllTail = !showAllTail"
            >
              {{ showAllTail ? 'Show fewer' : `Show all ${longTailRows.length}` }}
              <v-icon size="14">{{ showAllTail ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
          </div>
        </v-card>
      </template>
    </template>
  </div>
</template>

<script>
import HistoricalDateBanner from '@/components/HistoricalDateBanner.vue'
import { formatHistoricalDate } from '@/utils/historicalData'

const TAIL_PREVIEW = 12
// Below this many distinct sources we skip the "cited more than once" / long-tail
// split and just show a single flat table.
const SPLIT_THRESHOLD = 10

export default {
  name: 'SourcesTab',
  components: { HistoricalDateBanner },
  props: {
    queries: {
      type: Array,
      default: () => []
    },
    reviewEntityUrl: {
      type: String,
      default: ''
    },
    brandMatchAliases: {
      type: Array,
      default: () => []
    },
    primaryBrandName: {
      type: String,
      default: ''
    },
    selectedProvider: {
      type: String,
      default: null
    },
    providerLabel: {
      type: String,
      default: ''
    },
    scannedAt: {
      type: String,
      default: null
    },
    selectedHistoricalDate: {
      type: String,
      default: null
    },
    loadingSnapshot: {
      type: Boolean,
      default: false
    },
    snapshotLoadTarget: {
      type: String,
      default: null
    },
    showSnapshotBanner: {
      type: Boolean,
      default: false
    },
    snapshotError: {
      type: String,
      default: null
    }
  },
  emits: ['switch-provider', 'historical-date-cleared'],
  data() {
    return {
      TAIL_PREVIEW,
      showAllTail: false
    }
  },
  computed: {
    isGrounded() {
      return this.selectedProvider === 'google_grounded'
    },
    nonGroundedCopy() {
      if (this.selectedProvider === 'openai') {
        return {
          title: 'ChatGPT doesn’t show its sources',
          line1:
            'ChatGPT answers from what it learned in training rather than reading the ' +
            'web as it responds, so there are no sources to report — on any scan, not ' +
            'just this one.',
          line2:
            'Gemini (Grounded) searches the web for each answer and tells us what it ' +
            'read. Switch providers to see which sites are shaping what AI says about you.'
        }
      }
      return {
        title: 'This provider doesn’t show its sources',
        line1:
          'This provider answers from training data rather than reading the web as it ' +
          'responds, so there are no sources to report — on any scan, not just this one.',
        line2:
          'Gemini (Grounded) searches the web for each answer and tells us what it read. ' +
          'Switch providers to see which sites are shaping what AI says about you.'
      }
    },

    // [{ domain, finalUrl, queryId }] — one entry per attributable web citation,
    // no dedupe. Citations that resolve only to the Gemini redirect shell are
    // dropped here so they never reach the totals or the table.
    webCitations() {
      const out = []
      for (const query of this.queries || []) {
        if (!Array.isArray(query.citations)) continue
        for (const citation of query.citations) {
          if (!citation || citation.type !== 'web') continue
          const domain = this.resolveDomain(citation)
          if (!domain) continue
          out.push({
            domain,
            finalUrl: this.nonRedirectUrl(citation.final_url),
            queryId: query.id
          })
        }
      }
      return out
    },
    totalCites() {
      return this.webCitations.length
    },
    ownedDomains() {
      const set = new Set()
      const fromUrl = this.extractDomain(this.reviewEntityUrl)
      if (fromUrl) set.add(fromUrl)
      for (const alias of this.brandMatchAliases || []) {
        const parsed = this.parseBareDomain(alias)
        if (parsed) set.add(parsed)
      }
      return set
    },

    // Aggregated per-domain rows, owned pinned to the top, then by cites desc.
    domainRows() {
      const map = new Map()
      for (const c of this.webCitations) {
        let row = map.get(c.domain)
        if (!row) {
          row = { domain: c.domain, cites: 0, queryIds: new Set(), url: '' }
          map.set(c.domain, row)
        }
        row.cites += 1
        row.queryIds.add(c.queryId)
        // Prefer the backend-resolved destination; otherwise link to the
        // domain root rather than the opaque Gemini redirect shell.
        if (!row.url && c.finalUrl) row.url = c.finalUrl
      }

      const total = this.totalCites
      const rows = Array.from(map.values()).map((r) => {
        const queries = r.queryIds.size
        return {
          key: r.domain,
          domain: r.domain,
          cites: r.cites,
          queries,
          share: total ? r.cites / total : 0,
          url: r.url || `https://${r.domain}`,
          owned: this.ownedDomains.has(r.domain),
          concentrated: r.cites >= 3 && queries === 1
        }
      })

      rows.sort(
        (a, b) =>
          b.cites - a.cites ||
          b.queries - a.queries ||
          a.domain.localeCompare(b.domain)
      )

      const owned = rows.filter((r) => r.owned)
      const rest = rows.filter((r) => !r.owned)
      return [...owned, ...rest]
    },
    siteCount() {
      return this.domainRows.length
    },
    thirdPartySiteCount() {
      return this.domainRows.filter((r) => !r.owned).length
    },
    queryCount() {
      return new Set(this.webCitations.map((c) => c.queryId)).size
    },
    ownedRows() {
      return this.domainRows.filter((r) => r.owned)
    },
    ownedCites() {
      return this.ownedRows.reduce((sum, r) => sum + r.cites, 0)
    },
    ownedShare() {
      return this.totalCites ? this.ownedCites / this.totalCites : 0
    },
    ownedPrimaryDomain() {
      if (this.ownedRows.length) return this.ownedRows[0].domain
      return this.extractDomain(this.reviewEntityUrl) || ''
    },

    hasLongTail() {
      return (
        this.domainRows.length >= SPLIT_THRESHOLD &&
        this.domainRows.some((r) => r.cites === 1) &&
        this.domainRows.some((r) => r.cites > 1)
      )
    },
    mainRows() {
      return this.hasLongTail
        ? this.domainRows.filter((r) => r.cites > 1)
        : this.domainRows
    },
    maxMainCites() {
      return this.mainRows.reduce((max, r) => Math.max(max, r.cites), 0) || 1
    },
    mainCiteTotal() {
      return this.mainRows.reduce((sum, r) => sum + r.cites, 0)
    },
    // Loudest single-query source, for the footnote under the table.
    concentratedNote() {
      const rows = this.mainRows
        .filter((r) => r.concentrated)
        .sort((a, b) => b.cites - a.cites)
      return rows[0] || null
    },
    longTailRows() {
      return this.hasLongTail ? this.domainRows.filter((r) => r.cites === 1) : []
    },
    visibleLongTail() {
      return this.showAllTail
        ? this.longTailRows
        : this.longTailRows.slice(0, TAIL_PREVIEW)
    },

    scannedDateLabel() {
      if (this.selectedHistoricalDate) {
        return formatHistoricalDate(this.selectedHistoricalDate)
      }
      if (!this.scannedAt) return '—'
      const d = new Date(this.scannedAt)
      if (Number.isNaN(d.getTime())) return '—'
      return d.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    }
  },
  methods: {
    // Gemini hands us `citation.uri` as an opaque grounding-api-redirect URL; the
    // backend follows it during grounding validation and stores the real page as
    // `final_url`. Either can still be the redirect shell — no `final_url` because
    // verification threw, or a `uri` that never got resolved — and that shell must
    // never be treated as a real source (it would surface a row for
    // vertexaisearch.cloud.google.com and pull citations away from the true site).
    nonRedirectUrl(url) {
      if (!url) return ''
      if (/grounding-api-redirect|vertexaisearch\.cloud\.google\.com/i.test(url)) {
        return ''
      }
      return url
    },
    // Resolution order: backend-resolved destination (final_url) → citation.title
    // when it is a bare domain → hostname of citation.uri. A citation that
    // resolves to nothing but the redirect shell returns null and is dropped by
    // the caller rather than counted against a bogus domain.
    resolveDomain(citation) {
      const fromFinal = this.extractDomain(this.nonRedirectUrl(citation.final_url))
      if (fromFinal) return fromFinal
      const fromTitle = this.parseBareDomain(citation.title)
      if (fromTitle) return fromTitle
      return this.extractDomain(this.nonRedirectUrl(citation.uri))
    },
    extractDomain(url) {
      if (!url) return null
      let value = String(url).trim()
      if (!value) return null
      if (!/^https?:\/\//i.test(value)) value = `https://${value}`
      try {
        return new URL(value).hostname.replace(/^www\./, '').toLowerCase()
      } catch {
        return null
      }
    },
    parseBareDomain(value) {
      if (!value) return null
      const trimmed = String(value).trim().toLowerCase()
      if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmed)) return null
      return trimmed.replace(/^www\./, '')
    },
    formatPct(fraction) {
      const pct = (fraction || 0) * 100
      if (pct === 0) return '0%'
      if (pct < 1) return '<1%'
      return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`
    },
    barWidth(fraction) {
      const pct = Math.max(0, Math.min(1, fraction || 0)) * 100
      // Keep a hairline visible for any non-zero share.
      return pct > 0 && pct < 1.5 ? '1.5%' : `${pct}%`
    }
  }
}
</script>

<style scoped>
.mono {
  font-family: 'Courier New', ui-monospace, SFMono-Regular, Menlo, monospace;
}
.strong {
  color: var(--brand-ink-950, #0d1e35);
  font-weight: 700;
}
.muted {
  color: #6a85a0;
}

.section-eyebrow {
  margin-top: 30px;
  margin-bottom: 8px;
}

/* ── Empty states ─────────────────────────────────────────── */
.empty-card {
  padding: 52px 32px;
}
.empty-inner {
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
}
.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty-icon--muted {
  background: var(--brand-sand-100, #f7f4ec);
}
.empty-icon--warning {
  background: var(--brand-amber-100, #fef3d9);
}
.empty-title {
  font-family: 'Lora', serif;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--brand-ink-950, #0d1e35);
}
.empty-title--sm {
  font-size: 19px;
}
.empty-body {
  font-size: 14.5px;
  line-height: 1.6;
  color: #5b6d84;
  margin: 0;
}

/* ── Hero ─────────────────────────────────────────────────── */
.hero-card {
  padding: 30px 32px;
}
.hero-top {
  display: flex;
  align-items: flex-end;
  gap: 28px;
  flex-wrap: wrap;
}
.hero-figure {
  flex-shrink: 0;
}
.hero-pct {
  font-family: 'Lora', serif;
  font-size: 60px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--brand-ocean-600, #2169a8);
}
.hero-caption {
  font-size: 14px;
  color: #2e4a6a;
  margin-top: 10px;
}
.hero-bar-wrap {
  flex-grow: 1;
  min-width: 320px;
}
.hero-bar {
  display: flex;
  height: 32px;
  border-radius: 5px;
  overflow: hidden;
  gap: 2px;
  background: #ded7c8;
}
.hero-bar__owned {
  background: var(--brand-teal-500, #1aacb0);
}
.hero-bar__rest {
  flex-grow: 1;
  background: #ded7c8;
}
.hero-bar__legend {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 9px;
  font-size: 12.5px;
}
.hero-stats {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #ece6d6;
  display: flex;
  gap: 36px;
  flex-wrap: wrap;
  font-size: 13.5px;
  color: #2e4a6a;
}
.hero-stats strong {
  color: var(--brand-ink-950, #0d1e35);
}

.section-eyebrow--muted {
  color: #6a85a0;
}

/* ── Source table ─────────────────────────────────────────── */
.table-card {
  overflow-x: auto;
}
.src-table {
  min-width: 520px;
}
.src-table__head,
.src-table__row {
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(0, 1fr) 56px 72px;
  align-items: center;
  gap: 16px;
  padding: 10px 24px;
}
.src-table__head {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #6a85a0;
  border-bottom: 1px solid #ece6d6;
}
.src-table__row {
  border-bottom: 1px solid #f2ede0;
  font-size: 13.5px;
}
.src-table__row:last-child {
  border-bottom: none;
}
.src-table__row--owned {
  background: #f0f8fd;
  border-bottom: 1px solid var(--brand-sand-300, #d8cebc);
}
.src-table__source {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.src-table__domain {
  font-size: 13.5px;
  color: var(--brand-ocean-600, #2169a8);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.src-table__domain:hover {
  text-decoration: underline;
}
.src-table__domain.strong {
  color: var(--brand-ink-950, #0d1e35);
}
.src-table__bar {
  min-width: 0;
}
.src-table__track {
  height: 14px;
  background: #f4f1e8;
  border-radius: 3px;
  overflow: hidden;
}
.src-table__fill {
  height: 100%;
  background: var(--brand-ocean-600, #2169a8);
  border-radius: 0 3px 3px 0;
}
.src-table__fill--owned {
  background: var(--brand-teal-500, #1aacb0);
}
.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.src-table__cites {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--brand-ink-950, #0d1e35);
}
.src-table__queries {
  font-size: 12.5px;
  color: #6a85a0;
}
.warn-text {
  color: #8b5f16;
  font-weight: 600;
}
.warn-mark {
  color: #8b5f16;
}
.src-table__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 24px 0;
  padding: 14px 0 4px;
  border-top: 1px solid #ece6d6;
  font-size: 12.5px;
  line-height: 1.5;
  color: #8b5f16;
}

/* ── Shared pill ──────────────────────────────────────────── */
.owned-pill {
  flex-shrink: 0;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-ocean-600, #2169a8);
  background: var(--brand-ocean-100, #daeefa);
  border-radius: 999px;
  padding: 2px 8px;
}

/* ── Long tail ────────────────────────────────────────────── */
.tail-card {
  padding: 24px 26px;
}
.tail-headline {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.tail-count {
  font-family: 'Lora', serif;
  font-size: 28px;
  font-weight: 600;
  color: var(--brand-ink-950, #0d1e35);
  line-height: 1;
}
.tail-sub {
  font-size: 14px;
  color: #2e4a6a;
}
.tail-body {
  font-size: 13.5px;
  line-height: 1.6;
  color: #5b6d84;
  margin: 0 0 14px;
}
.tail-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
}
.tail-chip {
  font-size: 12px;
  color: #2e4a6a;
  background: var(--brand-sand-100, #f7f4ec);
  border: 1px solid #ece6d6;
  border-radius: 999px;
  padding: 4px 11px;
  text-decoration: none;
}
.tail-chip:hover {
  border-color: var(--brand-ocean-600, #2169a8);
  color: var(--brand-ocean-600, #2169a8);
}
.tail-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  border: 1px solid var(--brand-sand-300, #d8cebc);
  border-radius: 999px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-ocean-600, #2169a8);
  cursor: pointer;
}
.tail-toggle:hover {
  background: var(--brand-sand-100, #f7f4ec);
}

@media (max-width: 600px) {
  .hero-pct {
    font-size: 48px;
  }
}
</style>
